<template>
  <div class="contact-page">
    <!-- ===== HERO ===== -->
    <section class="contact-hero">
      <div class="contact-hero-bg"></div>
      <div class="contact-hero-overlay"></div>
      <div class="contact-hero-content" :class="{ visible: heroVisible }">
        <span class="section-tag">Get In Touch</span>
        <h1>We're Happy to<br /><span>Hear From You</span></h1>
        <p>
          Our team is always ready to help you start your fitness journey. Reach
          out and we'll get back to you as soon as possible.
        </p>
      </div>
    </section>

    <!-- ===== FORM + INFO ===== -->
    <section class="contact-main">
      <div class="contact-grid">
        <!-- Info Side -->
        <div class="contact-info">
          <h2>Contact Information</h2>
          <p class="info-sub">Fill out the form or reach us directly.</p>

          <div class="info-items">
            <div
              class="info-item"
              v-for="item in contactInfo"
              :key="item.label"
            >
              <div class="info-icon" v-html="item.icon"></div>
              <div>
                <span class="info-label">{{ item.label }}</span>
                <p>{{ item.value }}</p>
              </div>
            </div>
          </div>

          <div class="contact-socials">
            <a
              href="https://www.facebook.com/share/1DSfTz1RD8/"
              class="s-btn"
              aria-label="Facebook"
            >
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path
                  d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"
                />
              </svg>
            </a>
            <a href="#" class="s-btn" aria-label="Instagram">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <rect x="2" y="2" width="20" height="20" rx="5" />
                <circle cx="12" cy="12" r="4" />
                <circle
                  cx="17.5"
                  cy="6.5"
                  r="1"
                  fill="currentColor"
                  stroke="none"
                />
              </svg>
            </a>
            <a href="#" class="s-btn" aria-label="TikTok">
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path
                  d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.1V9.01a6.34 6.34 0 000 12.68 6.34 6.34 0 006.33-6.34V8.69a8.18 8.18 0 004.78 1.52V6.74a4.85 4.85 0 01-1.01-.05z"
                />
              </svg>
            </a>
            <a href="#" class="s-btn" aria-label="YouTube">
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path
                  d="M22.54 6.42a2.78 2.78 0 00-1.95-1.96C18.88 4 12 4 12 4s-6.88 0-8.59.46A2.78 2.78 0 001.46 6.42 29 29 0 001 12a29 29 0 00.46 5.58 2.78 2.78 0 001.95 1.96C5.12 20 12 20 12 20s6.88 0 8.59-.46a2.78 2.78 0 001.95-1.96A29 29 0 0023 12a29 29 0 00-.46-5.58zM9.75 15.02V8.98L15.5 12l-5.75 3.02z"
                />
              </svg>
            </a>
          </div>
        </div>

        <!-- Form Side -->
        <div class="contact-form-wrap">
          <div class="form-success" v-if="sent">
            <div class="success-icon">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2.5"
              >
                <polyline points="20 6 9 17 4 12" />
              </svg>
            </div>
            <h3>Message Sent!</h3>
            <p>We'll get back to you as soon as possible ðŸ’ª</p>
            <button class="btn-primary" @click="sent = false">
              Send Another
            </button>
          </div>

          <form class="contact-form" v-else @submit.prevent="submitForm">
            <div class="form-row">
              <div class="form-group">
                <label>Full Name</label>
                <input
                  type="text"
                  v-model="form.name"
                  placeholder="Your full name"
                  required
                />
              </div>
              <div class="form-group">
                <label>Email Address</label>
                <input
                  type="email"
                  v-model="form.email"
                  placeholder="your@email.com"
                  required
                />
              </div>
            </div>
            <div class="form-group">
              <label>Phone (Optional)</label>
              <input
                type="tel"
                v-model="form.phone"
                placeholder="+20 xxx xxx xxxx"
              />
            </div>
            <div class="form-group">
              <label>Interested In</label>
              <select v-model="form.interest">
                <option value="">Select a program...</option>
                <option>Personal Training</option>
                <option>Monthly 450 EGP</option>
                <option>3 Months 1,200 EGP</option>
                <option>2 Person 850 EGP</option>
                <option>3 Person 1,250 EGP</option>
                <option>VIP 800 EGP</option>
                <option>Bodybuilding</option>
                <option>Boxing</option>
                <option>Kickboxing</option>
                <option>MMA</option>
                <option>Karate</option>
                <option>General Fitness</option>
              </select>
            </div>
            <div class="form-group">
              <label>Message</label>
              <textarea
                v-model="form.message"
                placeholder="Tell us about your fitness goals..."
                required
              ></textarea>
            </div>
            <button
              type="submit"
              class="submit-btn"
              :class="{ loading: submitting }"
            >
              <span v-if="!submitting">Send Message</span>
              <span v-else class="loader"></span>
            </button>
          </form>
        </div>
      </div>
    </section>

    <!-- ===== LOGO CTA ===== -->
    <section class="logo-cta">
      <div class="logo-cta-inner">
        <img src="@/assets/logo.png" alt="Venom Gym" class="cta-logo" />
        <div>
          <h2>Start Your Fitness Journey Today</h2>
          <p>
            We are always ready to help you achieve the best version of yourself
            ðŸ’ª
          </p>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
export default {
  name: "ContactView",
  data() {
    return {
      heroVisible: false,
      sent: false,
      submitting: false,
      form: { name: "", email: "", phone: "", interest: "", message: "" },
      contactInfo: [
        {
          label: "Address",
          value: "1 Street 18, Sidi Beshr Bahri, Alexandria",
          icon: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/></svg>`,
        },
        {
          label: "Phone",
          value: "+20 12 87679354",
          icon: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6A19.79 19.79 0 012.12 4.18 2 2 0 014.11 2h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z"/></svg>`,
        },
        {
          label: "Hours",
          value: "Sat – Thu: 6AM – 12AM | Fri: 8AM – 10PM",
          icon: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>`,
        },
      ],
    };
  },
  mounted() {
    setTimeout(() => {
      this.heroVisible = true;
    }, 100);
  },
  methods: {
    async submitForm() {
      this.submitting = true;
      await new Promise((r) => setTimeout(r, 800));

      const phone = "201287679354";
      const msg =
        `📋 *رسالة جديدة من موقع Venom Gym*\n\n` +
        `👤 *الاسم:* ${this.form.name}\n` +
        `📧 *الإيميل:* ${this.form.email}\n` +
        `📞 *التليفون:* ${this.form.phone || "—"}\n` +
        `🏋️ *الاهتمام:* ${this.form.interest || "—"}\n` +
        `💬 *الرسالة:* ${this.form.message}`;

      const url = `https://wa.me/${phone}?text=${encodeURIComponent(msg)}`;
      window.open(url, "_blank");

      this.submitting = false;
      this.sent = true;
      this.form = { name: "", email: "", phone: "", interest: "", message: "" };
    },
  },
};
</script>
<style scoped>
.contact-page {
  background: var(--black);
  color: var(--white);
}

.section-tag {
  display: inline-block;
  font-family: "Rajdhani", sans-serif;
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 4px;
  text-transform: uppercase;
  color: var(--red);
  margin-bottom: 12px;
  padding: 4px 14px;
  border: 1px solid rgba(204, 0, 0, 0.3);
  border-radius: 2px;
}

/* ===== HERO ===== */
.contact-hero {
  position: relative;
  min-height: 65vh;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}
.contact-hero-bg {
  position: absolute;
  inset: 0;
  background: url("https://images.unsplash.com/photo-1534438327276-14e5300c3a48?auto=format&fit=crop&w=1600&q=80")
    center/cover;
  filter: brightness(0.15) saturate(0.4);
  animation: heroPan 18s ease-in-out infinite alternate;
}
@keyframes heroPan {
  from {
    background-position: 40% center;
  }
  to {
    background-position: 60% center;
  }
}
.contact-hero-overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(
    135deg,
    rgba(120, 0, 0, 0.25),
    transparent 55%,
    rgba(5, 5, 5, 0.5)
  );
}
.contact-hero-content {
  position: relative;
  z-index: 2;
  text-align: center;
  max-width: 680px;
  padding: 80px 6% 0;
  opacity: 0;
  transform: translateY(24px);
  transition: all 0.9s ease;
}
.contact-hero-content.visible {
  opacity: 1;
  transform: translateY(0);
}
.contact-hero-content h1 {
  font-family: "Black Ops One", cursive;
  font-size: clamp(2.5rem, 6vw, 5rem);
  line-height: 1.05;
  margin: 10px 0 18px;
}
.contact-hero-content h1 span {
  color: var(--red);
}
.contact-hero-content p {
  color: rgba(240, 240, 240, 0.6);
  font-family: "Cairo", sans-serif;
  font-size: 1rem;
  line-height: 1.8;
}

/* ===== MAIN ===== */
.contact-main {
  padding: 80px 6%;
}
.contact-grid {
  display: grid;
  grid-template-columns: 380px 1fr;
  gap: 32px;
  max-width: 1200px;
  margin: 0 auto;
}

/* Info Side */
.contact-info {
  background: var(--steel);
  padding: 48px 36px;
  border-top: 2px solid var(--red);
  position: relative;
  overflow: hidden;
}
.contact-info::before {
  content: "";
  position: absolute;
  bottom: -60px;
  right: -60px;
  width: 200px;
  height: 200px;
  border-radius: 50%;
  background: rgba(204, 0, 0, 0.06);
}
.contact-info h2 {
  font-family: "Bebas Neue", cursive;
  font-size: 1.8rem;
  letter-spacing: 2px;
  margin-bottom: 8px;
}
.info-sub {
  color: var(--grey);
  font-size: 0.88rem;
  margin-bottom: 36px;
}

.info-items {
  display: flex;
  flex-direction: column;
  gap: 24px;
  margin-bottom: 36px;
}
.info-item {
  display: flex;
  align-items: flex-start;
  gap: 14px;
}
.info-icon {
  width: 42px;
  height: 42px;
  flex-shrink: 0;
  background: rgba(204, 0, 0, 0.12);
  border: 1px solid rgba(204, 0, 0, 0.25);
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--red);
}
.info-icon :deep(svg) {
  width: 18px;
  height: 18px;
}
.info-label {
  display: block;
  font-family: "Rajdhani", sans-serif;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 2px;
  text-transform: uppercase;
  color: var(--red);
  margin-bottom: 3px;
}
.info-item p {
  color: var(--grey);
  font-size: 0.85rem;
  line-height: 1.5;
}

.contact-socials {
  display: flex;
  gap: 10px;
}
.s-btn {
  width: 38px;
  height: 38px;
  border: 1px solid rgba(204, 0, 0, 0.3);
  border-radius: 5px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--grey);
  transition: all 0.3s;
  text-decoration: none;
}
.s-btn svg {
  width: 15px;
  height: 15px;
}
.s-btn:hover {
  background: var(--red);
  border-color: var(--red);
  color: #fff;
  transform: translateY(-3px);
}

/* Form Side */
.contact-form-wrap {
  background: var(--steel);
  padding: 48px 40px;
}
.contact-form {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.form-group label {
  font-family: "Rajdhani", sans-serif;
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 2px;
  text-transform: uppercase;
  color: var(--grey);
}
.form-group input,
.form-group textarea,
.form-group select {
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.08);
  color: var(--white);
  padding: 13px 16px;
  font-family: "Cairo", sans-serif;
  font-size: 0.9rem;
  outline: none;
  transition: border-color 0.3s, box-shadow 0.3s;
  border-radius: 2px;
}
.form-group input::placeholder,
.form-group textarea::placeholder {
  color: rgba(255, 255, 255, 0.2);
}
.form-group input:focus,
.form-group textarea:focus,
.form-group select:focus {
  border-color: rgba(204, 0, 0, 0.5);
  box-shadow: 0 0 0 3px rgba(204, 0, 0, 0.08);
  background: rgba(204, 0, 0, 0.04);
}
.form-group select option {
  background: #1a1a1a;
  color: var(--white);
}
.form-group textarea {
  resize: vertical;
  min-height: 130px;
}

.submit-btn {
  background: var(--red);
  color: #fff;
  border: none;
  padding: 16px;
  cursor: pointer;
  font-family: "Bebas Neue", cursive;
  font-size: 1.2rem;
  letter-spacing: 3px;
  clip-path: polygon(8px 0%, 100% 0%, calc(100% - 8px) 100%, 0% 100%);
  transition: all 0.3s;
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
}
.submit-btn::before {
  content: "";
  position: absolute;
  inset: 0;
  background: rgba(255, 255, 255, 0.08);
  transform: translateX(-100%);
  transition: transform 0.4s ease;
}
.submit-btn:hover {
  background: var(--red-glow);
  transform: translateY(-2px);
  box-shadow: 0 10px 30px rgba(204, 0, 0, 0.4);
}
.submit-btn:hover::before {
  transform: translateX(100%);
}
.submit-btn.loading {
  opacity: 0.8;
  pointer-events: none;
}

.loader {
  width: 20px;
  height: 20px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: #fff;
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
}
@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* Success */
.form-success {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  min-height: 340px;
  gap: 16px;
}
.success-icon {
  width: 72px;
  height: 72px;
  border-radius: 50%;
  background: rgba(204, 0, 0, 0.12);
  border: 2px solid var(--red);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--red);
  animation: popIn 0.5s cubic-bezier(0.34, 1.56, 0.64, 1);
}
.success-icon svg {
  width: 32px;
  height: 32px;
}
@keyframes popIn {
  from {
    transform: scale(0);
  }
  to {
    transform: scale(1);
  }
}
.form-success h3 {
  font-family: "Bebas Neue", cursive;
  font-size: 2rem;
  letter-spacing: 2px;
}
.form-success p {
  color: var(--grey);
}
.btn-primary {
  background: var(--red);
  color: #fff;
  border: none;
  padding: 13px 32px;
  cursor: pointer;
  font-family: "Bebas Neue", cursive;
  font-size: 1rem;
  letter-spacing: 2px;
  clip-path: polygon(8px 0%, 100% 0%, calc(100% - 8px) 100%, 0% 100%);
  transition: all 0.3s;
  margin-top: 8px;
}
.btn-primary:hover {
  background: var(--red-glow);
}

/* ===== LOGO CTA ===== */
.logo-cta {
  background: var(--steel);
  border-top: 1px solid rgba(204, 0, 0, 0.2);
  padding: 60px 6%;
}
.logo-cta-inner {
  max-width: 700px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  gap: 32px;
}
.cta-logo {
  height: 100px;
  width: auto;
  flex-shrink: 0;
  filter: drop-shadow(0 0 20px rgba(204, 0, 0, 0.6));
  animation: pulse 3s ease infinite;
}
@keyframes pulse {
  0%,
  100% {
    filter: drop-shadow(0 0 14px rgba(204, 0, 0, 0.5));
  }
  50% {
    filter: drop-shadow(0 0 28px rgba(204, 0, 0, 0.9));
  }
}
.logo-cta-inner h2 {
  font-family: "Black Ops One", cursive;
  font-size: clamp(1.5rem, 3vw, 2.2rem);
  margin-bottom: 10px;
}
.logo-cta-inner p {
  color: var(--grey);
  font-size: 0.95rem;
  line-height: 1.6;
}

@media (max-width: 900px) {
  .contact-grid {
    grid-template-columns: 1fr;
  }
  .form-row {
    grid-template-columns: 1fr;
  }
  .logo-cta-inner {
    flex-direction: column;
    text-align: center;
  }
}
</style>
